import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../../api/axiosInstance";
import { readStoredUser } from "../../../utils/sessionUser";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { getDocumentTranslator } from "./documentPopupMessages";
import useItemDialogFocus from "../useItemDialogFocus";
import PurchasingDocumentDialog from "./PurchasingDocumentDialog";
import "./PurchasingDocumentPopups.css";
import "./RequisitionTemplatesDialog.css";

const TEMPLATE_MODAL_VIEWS = {
  MENU: "menu",
  SAVE: "save",
  LOAD: "load",
};

const formatTemplateDate = (value, locale) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function RequisitionTemplatesDialog({ lines, businessUnitId, onApply, onClose, onNotice, confirmReplacement = false }) {
  const { themeId, languageId } = usePmsTheme(), t = getDocumentTranslator(languageId);
  const locale = languageId === "es" ? "es-ES" : "en-GB";
  const [ownerId] = useState(() => readStoredUser()?.id);
  const request = useRef(null), busy = useRef(false);
  const [replacement, setReplacement] = useState(null);
  const closeTemplateModal = () => { if (!busy.current) onClose(); };
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { request.current?.abort(); document.body.style.overflow = previous; };
  }, []);
  const requestConfig = () => {
    const user = readStoredUser();
    if (String(user?.id) !== String(ownerId) || String(user?.active_business_unit_id) !== String(businessUnitId)) throw new Error(t("The session or business unit changed. Reopen this requisition before saving a template."));
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    return { signal: controller.signal, pilcoreExpectedUserId: ownerId, headers: { "X-Pilcore-Business-Unit-Id": businessUnitId } };
  };
  const [templateModalView, setTemplateModalView] = useState(TEMPLATE_MODAL_VIEWS.MENU);
  const [templateDraftName, setTemplateDraftName] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateModalError, setTemplateModalError] = useState("");
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSubmitting, setTemplateSubmitting] = useState(false);
  const dialogRef = useItemDialogFocus(!replacement, templateSubmitting, closeTemplateModal);
  const filteredTemplateOptions = useMemo(() => {
    const query = String(templateSearch || "").trim().toLowerCase();
    if (!query) return templateOptions;
    return (templateOptions || []).filter((template) =>
      String(template?.name || "").toLowerCase().includes(query)
    );
  }, [templateOptions, templateSearch]);

  const openTemplateMenu = () => {
    request.current?.abort();
    setTemplateModalError("");
    setTemplateDraftName("");
    setTemplateSearch("");
    setSelectedTemplateId("");
    setTemplateLoading(false);
    setTemplateSubmitting(false);
    setTemplateModalView(TEMPLATE_MODAL_VIEWS.MENU);
  };

  const openSaveTemplateView = () => {
    setTemplateModalError("");
    setTemplateDraftName("");
    setTemplateSearch("");
    setSelectedTemplateId("");
    setTemplateLoading(false);
    setTemplateSubmitting(false);
    setTemplateModalView(TEMPLATE_MODAL_VIEWS.SAVE);
  };

  const openLoadTemplateView = async () => {
    setTemplateModalView(TEMPLATE_MODAL_VIEWS.LOAD);
    setTemplateModalError("");
    setTemplateSearch("");
    setSelectedTemplateId("");
    setTemplateOptions([]);
    setTemplateLoading(true);
    let config;
    try {
      config = requestConfig();
      const res = await axios.get("/api/purchasing/templates", config);
      if (config.signal.aborted) return;
      const templates = (Array.isArray(res.data) ? res.data : []).filter((template) => Number(template.business_unit_id) === Number(businessUnitId));
      setTemplateOptions(templates);
      if (templates.length) {
        setSelectedTemplateId(String(templates[0].id));
      }
    } catch (err) {
      if (config?.signal.aborted) return;
      setTemplateModalError(t("Error loading templates."));
    } finally {
      if (!config?.signal.aborted) setTemplateLoading(false);
    }
  };

  const saveTemplate = async () => {
    const name = String(templateDraftName || "").trim();
    if (!name) {
      setTemplateModalError(t("Enter a name for this template."));
      return;
    }
    if (busy.current) return;
    busy.current = true;
    setTemplateSubmitting(true);
    setTemplateModalError("");
    try {
      await axios.post("/api/purchasing/templates", {
        business_unit_id: businessUnitId,
        name,
        lines: lines || [],
      }, requestConfig());
      if (request.current?.signal.aborted) return;
      onNotice(t("Template \"{0}\" saved.", { 0: name }));
      onClose();
    } catch (err) {
      console.error("[RequisitionTemplatesDialog] save template error:", err);
      setTemplateModalError(t(err?.response?.data?.code === "REQUISITION_UNIT_CONFIGURATION_STALE"
        ? "The item's units or packaging have changed. Reload the requisition and review its quantities before saving."
        : err?.response?.data?.error || "Error saving template."));
    } finally {
      busy.current = false;
      setTemplateSubmitting(false);
    }
  };

  const loadTemplate = async (confirmed = false) => {
    const selected = (templateOptions || []).find(
      (template) => String(template.id) === String(selectedTemplateId)
    );
    if (!selected) {
      setTemplateModalError(t("Select a saved template first."));
      return;
    }
    if (confirmReplacement && !confirmed) { setReplacement(selected); return; }
    setReplacement(null);
    if (busy.current) return;
    busy.current = true;
    setTemplateSubmitting(true);
    setTemplateModalError("");
    try {
      await onApply(selected, requestConfig());
      if (request.current?.signal.aborted) return;
      onNotice(t("Template \"{0}\" loaded.", { 0: selected.name }));
      onClose();
    } catch (err) {
      if (request.current?.signal.aborted) return;
      setTemplateModalError(err?.response?.data?.error || err.message || t("Error loading template."));
    } finally {
      busy.current = false;
      setTemplateSubmitting(false);
    }
  };

  return createPortal(<div className="pilcore-purchasing-ui pilcore-document-popup rqa-templateOverlay" data-theme={themeId}
    onMouseDown={(event) => { if (event.target === event.currentTarget) closeTemplateModal(); }}>
      {(
        <div className="rqa-template" onMouseDown={(e) => { if (e.target === e.currentTarget) closeTemplateModal(); }}>
          <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={t("Templates")} tabIndex={-1} className={`rqa-templateCard ${templateModalView === TEMPLATE_MODAL_VIEWS.LOAD ? "rqa-templateCard--wide" : ""}`}>
            <div className="rqa-templateHead">
              <div>
                <h6>
                  {templateModalView === TEMPLATE_MODAL_VIEWS.SAVE
                    ? t("Save Template")
                    : templateModalView === TEMPLATE_MODAL_VIEWS.LOAD
                      ? t("Load Template")
                      : t("Templates")}
                </h6>
                <p>
                  {templateModalView === TEMPLATE_MODAL_VIEWS.SAVE
                    ? t("Name the current requisition setup and save it as a reusable Pilcore template.")
                    : templateModalView === TEMPLATE_MODAL_VIEWS.LOAD
                      ? t("Choose one of your saved templates to replace the current requisition lines.")
                      : t("Save the current requisition as a reusable template or load an existing one.")}
                </p>
              </div>
              <button
                type="button"
                className="rqa-closeBtn"
                aria-label={t("Close templates")}
                onClick={closeTemplateModal}
              >
                X
              </button>
            </div>

            <div className="rqa-templateBody">
              {templateModalError ? <div className="rqa-alert" style={{ marginTop: 0 }}>{templateModalError}</div> : null}

              {templateModalView === TEMPLATE_MODAL_VIEWS.MENU && (
                <div className="rqa-templateActions">
                  <button type="button" className="rqa-btn rqa-btn--cyan" onClick={openSaveTemplateView}>{t("Save Template")}</button>
                  <button type="button" className="rqa-btn rqa-btn--ghost" onClick={openLoadTemplateView}>{t("Load Template")}</button>
                  <button type="button" className="rqa-btn rqa-btn--ghost" onClick={closeTemplateModal}>{t("Close")}</button>
                </div>
              )}

              {templateModalView === TEMPLATE_MODAL_VIEWS.SAVE && (
                <>
                  <div>
                    <label className="rqa-label" htmlFor="requisition-template-name">{t("Template Name")}</label>
                    <input
                      id="requisition-template-name"
                      className="form-control"
                      autoFocus
                      value={templateDraftName}
                      onChange={(e) => setTemplateDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveTemplate();
                        }
                      }}
                      placeholder={t("Example: Weekly breakfast replenishment")}
                    />
                  </div>
                  <div className="rqa-templateHint">{t("We will save the current requisition lines exactly as they are now, so you can reuse them later in one click.")}</div>
                  <div className="rqa-templateFooter">
                    <button type="button" className="rqa-btn rqa-btn--ghost" onClick={openTemplateMenu} disabled={templateSubmitting}>{t("Back")}</button>
                    <button type="button" className="rqa-btn rqa-btn--primary" onClick={saveTemplate} disabled={templateSubmitting}>
                      {templateSubmitting ? t("Saving...") : t("Save Template")}
                    </button>
                  </div>
                </>
              )}

              {templateModalView === TEMPLATE_MODAL_VIEWS.LOAD && (
                <>
                  <div className="rqa-templateToolbar">
                    <input
                      className="form-control"
                      value={templateSearch}
                      onChange={(e) => setTemplateSearch(e.target.value)}
                      placeholder={t("Search template by name...")}
                    />
                  </div>

                  {templateLoading ? (
                    <div className="rqa-templateState">{t("Loading saved templates...")}</div>
                  ) : filteredTemplateOptions.length ? (
                    <div className="rqa-templateList">
                      {filteredTemplateOptions.map((template) => {
                        const lineCount = Array.isArray(template?.lines) ? template.lines.length : 0;
                        const stampedAt = formatTemplateDate(template?.updated_at || template?.created_at, locale);
                        const isSelected = String(template.id) === String(selectedTemplateId);
                        return (
                          <button
                            key={template.id}
                            type="button"
                            className={`rqa-templateOption ${isSelected ? "rqa-templateOption--active" : ""}`} aria-pressed={isSelected}
                            onClick={() => setSelectedTemplateId(String(template.id))}
                          >
                            <div className="rqa-templateOptionTitle">
                              <span>{template.name || t("Template #{0}", { 0: template.id })}</span>
                              <span className="rqa-templateOptionPill">
                                {lineCount} {languageId === "es" ? (lineCount === 1 ? "artículo" : "artículos") : (lineCount === 1 ? "line" : "lines")}
                              </span>
                            </div>
                            <div className="rqa-templateOptionMeta">
                              {stampedAt ? t("Updated {0}", { 0: stampedAt }) : t("Saved template")}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rqa-templateState">
                      {templateSearch
                        ? t("No saved templates match that search.")
                        : t("No templates saved yet.")}
                    </div>
                  )}

                  <div className="rqa-templateFooter">
                    <button type="button" className="rqa-btn rqa-btn--ghost" onClick={openTemplateMenu} disabled={templateSubmitting}>{t("Back")}</button>
                    <button
                      type="button"
                      className="rqa-btn rqa-btn--primary"
                      onClick={() => loadTemplate()}
                      disabled={templateSubmitting || templateLoading || !selectedTemplateId}
                    >
                      {templateSubmitting ? t("Loading...") : t("Load Template")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}


    <PurchasingDocumentDialog isOpen={Boolean(replacement)} title={t("Load Template")}
      message={languageId === "es" ? "Se sustituirán todos los productos del carrito por los de la plantilla. ¿Continuar?" : "All products in the cart will be replaced by the template. Continue?"}
      onConfirm={() => loadTemplate(true)} onClose={() => setReplacement(null)} confirmLabel={t("Load Template")} />
  </div>, document.body);
}
